import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, ExternalLink, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { trajectColumn, type TrajectKind } from '@/lib/traject';
import { isVerlopen, lokaleDag } from '@/features/offertes/data/status';

type Doel = Exclude<TrajectKind, 'geen'>;

interface EntityQuotesProps {
  /** Welk traject: 'project', 'kavel' of 'opportunity'. */
  scope: Doel;
  /** Id van de entiteit. */
  entityId: string;
}

type QuoteRow = {
  id: string;
  quote_number: string;
  title: string;
  status: string;
  total_amount: number | null;
  valid_until: string | null;
  quote_date: string | null;
  created_at: string;
};

const STATUS_LABELS: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  draft: { label: 'Concept', variant: 'secondary' },
  sent: { label: 'Verstuurd', variant: 'default' },
  accepted: { label: 'Geaccepteerd', variant: 'default' },
  declined: { label: 'Afgewezen', variant: 'destructive' },
};

const LEEG_TEKST: Record<Doel, string> = {
  project: 'Nog geen offerte aan dit project gekoppeld.',
  kavel: 'Nog geen offerte aan deze kavel gekoppeld.',
  opportunity: 'Nog geen offerte aan deze opportunity gekoppeld.',
};

const formatAmount = (amount: number | null) =>
  amount == null
    ? '-'
    : new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);

/**
 * Offertes van één traject, voor op een detailpagina.
 *
 * T1.9 bouwde dit voor de projectpagina; T2.2 maakte er drie doelen van omdat
 * `quotes` sinds migratie 032 ook aan een Kavel of Opportunity kan hangen. De
 * drie verschillen alleen in de kolom waarop gefilterd wordt — de rest van de
 * lijst is identiek, dus dat is één prop en geen tweede bestand.
 */
export function EntityQuotes({ scope, entityId }: EntityQuotesProps) {
  const kolom = trajectColumn(scope);

  const { data: quotes = [], isLoading, isError } = useQuery({
    queryKey: ['entity-quotes', scope, entityId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('quotes')
        .select('id, quote_number, title, status, total_amount, valid_until, quote_date, created_at')
        .eq(kolom, entityId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as QuoteRow[];
    },
    enabled: Boolean(entityId),
  });

  const vandaag = lokaleDag();
  const totaalGeaccepteerd = quotes
    .filter((q) => q.status === 'accepted')
    .reduce((sum, q) => sum + (q.total_amount ?? 0), 0);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Offertes ({quotes.length})
          </CardTitle>
          <Button size="sm" variant="outline" asChild>
            <Link to="/crm/quotes">
              <Plus className="h-4 w-4 mr-1.5" />
              Naar offertes
            </Link>
          </Button>
        </div>
        {totaalGeaccepteerd > 0 && (
          <p className="text-sm text-muted-foreground">
            Geaccepteerd: {formatAmount(totaalGeaccepteerd)}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-center text-muted-foreground py-4">Laden...</p>
        ) : isError ? (
          <p className="text-center text-destructive py-4">Fout bij laden van offertes</p>
        ) : quotes.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">{LEEG_TEKST[scope]}</p>
        ) : (
          <div className="space-y-2">
            {quotes.map((quote) => {
              const status = STATUS_LABELS[quote.status] ?? { label: quote.status, variant: 'outline' as const };
              // Afgeleid op de lokale dag: de laatste geldige dag is nog geldig (ADR 0018).
              const verlopen = isVerlopen(quote, vandaag);

              return (
                <Link
                  key={quote.id}
                  to={`/crm/quotes/${quote.id}`}
                  className="flex items-start justify-between gap-3 rounded-lg border p-3 transition-colors hover:bg-accent/50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{quote.quote_number}</span>
                      <Badge variant={status.variant} className="text-xs">{status.label}</Badge>
                      {verlopen && <Badge variant="destructive" className="text-xs">Verlopen</Badge>}
                    </div>
                    <h4 className="truncate text-sm font-medium">{quote.title}</h4>
                    <p className="text-xs text-muted-foreground">
                      {quote.quote_date
                        ? format(new Date(quote.quote_date), 'd MMM yyyy', { locale: nl })
                        : format(new Date(quote.created_at), 'd MMM yyyy', { locale: nl })}
                      {quote.valid_until &&
                        ` - geldig tot ${format(new Date(quote.valid_until), 'd MMM yyyy', { locale: nl })}`}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-sm font-medium">{formatAmount(quote.total_amount)}</span>
                    <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
